import { Location } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    OnDestroy,
    QueryList,
    TemplateRef,
    ViewChildren,
} from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { DialogService } from "@dtm-frontend/shared/ui";
import { EllipsifyHtmlDirective, LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { KpoNewsSlideDirective } from "./kpo-news-slide.directive";
import { NewsDialogComponent } from "./news-dialog/news-dialog.component";

interface KpoNewsComponentState {
    currentSlide: number;
    news: KpoNewsSlideDirective[];
    shouldShowDetailsButton: boolean;
}

const NEWS_ID_QUERY_PARAM = "newsId";

@UntilDestroy()
@Component({
    selector: "dtm-main-page-lib-kpo-news",
    templateUrl: "./kpo-news.component.html",
    styleUrls: ["./kpo-news.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class KpoNewsComponent implements AfterViewInit, OnDestroy {
    @ContentChildren(KpoNewsSlideDirective) protected set slides(value: QueryList<KpoNewsSlideDirective>) {
        this.localStore.patchState(({ currentSlide }) => ({
            news: value.toArray(),
            currentSlide: Math.max(Math.min(currentSlide, value.length - 1), 0),
        }));
    }
    @ViewChildren("previewContentEllipsisInfo") protected previewContentEllipsisInfoElements: QueryList<EllipsifyHtmlDirective> | undefined;

    protected readonly currentSlide$ = this.localStore.selectByKey("currentSlide");
    protected readonly news$ = this.localStore.selectByKey("news");
    protected readonly shouldShowDetailsButton$ = this.localStore.selectByKey("shouldShowDetailsButton");
    private dialogInstance: MatDialogRef<NewsDialogComponent> | undefined;

    constructor(
        private readonly localStore: LocalComponentStore<KpoNewsComponentState>,
        private readonly dialogService: DialogService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly location: Location,
        private readonly cdr: ChangeDetectorRef
    ) {
        localStore.setState({ currentSlide: 0, news: [], shouldShowDetailsButton: false });
    }

    public ngOnDestroy(): void {
        this.dialogInstance?.close();
    }

    public ngAfterViewInit(): void {
        this.onHasEllipsisChanged();
        this.handleNewsIdParamChange();
        // NOTE: Sometimes after navigating back to the page CD isn't triggered properly for details button
        this.cdr.detectChanges();
    }

    public previousSlide(): void {
        this.localStore.patchState(({ currentSlide, news }) => {
            const newCurrentSlide = currentSlide <= 0 ? news.length - 1 : currentSlide - 1;
            const shouldShowDetailsButton = this.shouldShowDetailsButton(news, newCurrentSlide);

            return {
                currentSlide: newCurrentSlide,
                shouldShowDetailsButton,
            };
        });
    }

    public nextSlide(): void {
        this.localStore.patchState(({ currentSlide, news }) => {
            const newCurrentSlide = currentSlide >= news.length - 1 ? 0 : currentSlide + 1;
            const shouldShowDetailsButton = this.shouldShowDetailsButton(news, newCurrentSlide);

            return {
                currentSlide: newCurrentSlide,
                shouldShowDetailsButton,
            };
        });
    }

    private shouldShowDetailsButton(news: KpoNewsSlideDirective[], slideIndex: number): boolean {
        if (news[slideIndex].previewContent) {
            return true;
        }

        return !!this.previewContentEllipsisInfoElements?.toArray()[slideIndex]?.hasEllipsis;
    }

    public openDetails(skipNavigation = false) {
        const slideData = this.localStore.selectSnapshotByKey("news")[this.localStore.selectSnapshotByKey("currentSlide")];
        if (!slideData) {
            return;
        }

        this.dialogInstance = this.dialogService.open(NewsDialogComponent, {
            data: slideData,
        });

        this.dialogInstance
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe(() =>
                this.location.go(
                    this.router
                        .createUrlTree(["."], {
                            relativeTo: this.route,
                            queryParams: { [NEWS_ID_QUERY_PARAM]: undefined },
                            queryParamsHandling: "merge",
                        })
                        .toString()
                )
            );

        if (!slideData.id || skipNavigation) {
            return;
        }

        this.location.go(
            this.router
                .createUrlTree(["."], {
                    relativeTo: this.route,
                    queryParams: { [NEWS_ID_QUERY_PARAM]: slideData.id },
                    queryParamsHandling: "merge",
                })
                .toString()
        );
    }

    private handleNewsIdParamChange() {
        this.route.queryParams.pipe(untilDestroyed(this)).subscribe(({ [NEWS_ID_QUERY_PARAM]: newsId }) => {
            if (!newsId) {
                return;
            }
            const foundSlideIndex = this.localStore.selectSnapshotByKey("news").findIndex(({ id }) => id === newsId);

            if (foundSlideIndex !== -1) {
                this.localStore.patchState(({ news }) => ({
                    currentSlide: news.findIndex((slide) => slide.id === newsId),
                }));

                this.openDetails(true);
            }
        });
    }

    protected isString(element: TemplateRef<unknown> | string | undefined): element is string {
        return typeof element === "string";
    }

    protected getPreviewTemplate(element: KpoNewsSlideDirective): TemplateRef<unknown> | null {
        if (element.previewContent instanceof TemplateRef) {
            return element.previewContent;
        }

        if (this.isString(element.previewContent)) {
            return null;
        }

        return element.template;
    }

    protected onHasEllipsisChanged(): void {
        this.localStore.patchState(({ news, currentSlide }) => ({
            shouldShowDetailsButton: this.shouldShowDetailsButton(news, currentSlide),
        }));
    }
}
